<template>
    <div class="flex align-items-center alert" role="alert" :class="[classes, `alert-${type}`]">
        <template v-if="showIcon">
            <svg xmlns="http://www.w3.org/2000/svg" class="d-none">
                <symbol id="check-circle-fill" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                </symbol>
                <symbol id="info-fill" viewBox="0 0 16 16">
                    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                </symbol>
                <symbol id="exclamation-triangle-fill" viewBox="0 0 16 16">
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                </symbol>
            </svg>
            
            <svg
                v-if="type === 'primary' || type === 'info'" 
                class="bi flex-shrink-0 me-2" 
                width="16px" height="16px"
                role="img" aria-label="Info:"
            >
                <use xlink:href="#info-fill" :style="{ fill: computedType }" />
            </svg>
            <svg
                v-else-if="type === 'success'" 
                class="bi flex-shrink-0 me-2" 
                width="16px" 
                height="16px"
                role="img" aria-label="Success:"
            >
                <use xlink:href="#check-circle-fill" />
            </svg>
            <svg
                v-else-if="type === 'secondary'" 
                class="bi flex-shrink-0 me-2" 
                width="16px" 
                height="16px"
                role="img" aria-label="Success:"
            >
                <use xlink:href="#info-fill" style="fill: #0000009c;" />
            </svg>
            <svg 
                v-else-if="type === 'danger'" 
                class="bi flex-shrink-0 me-2" 
                width="16px" 
                height="16px" 
                role="img" 
                aria-label="Danger:"
            >
                <use xlink:href="#exclamation-triangle-fill" style="fill: #58151c;" />
            </svg>
            <svg
                v-else-if="type === 'warning'" 
                width="16px" 
                height="16px" 
                fill="#664d03"
                xmlns="http://www.w3.org/2000/svg"
                class="bi bi-exclamation-triangle-fill flex-shrink-0 me-2" v
                iewBox="0 0 16 16" 
                role="img" 
                aria-label="Warning:"
            >
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
            </svg>
        </template>

        <div class="ml--5">
            <p v-if="desc" :class="`text--${size}`">
                {{ desc }}
            </p>
            <slot v-else name="text" />
        </div>
    </div>
</template>

<script>
export default {
    name: "Alert",

    props: {
        desc: {
            type: String,
            default: "",
        },

        showIcon: {
            type: Boolean,
            default: true,
        },

        classes: {
            type: String,
            required: false,
            default: "",
        },

        type: {
            type: String,
            default: "",
            validator: (value) => {
                return ["info", "secondary", "secondary-grey", "danger", "primary", "warning"].includes(value);
            },
        },
        size: {
            type: String,
            default: "xs",
            validator: (value) => {
                return ["xs", "sm", "md", "lg"].includes(value);
            },
        },
    },

    computed: {
        computedType() {
            switch(this.type) {
            case "info":
                return "#57584e";
            case "primary":
                return "#3697a2";
            default:
                return "";
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.alert {
    border: 1px dotted transparent;
    
    &-primary {
        border-color: #a6dce2 !important;
        color: #007880 !important;
        background-color: #ebfafb !important;
    }

    &-secondary {
        border-color: #00000000 !important;
        color: #0000009c !important;
        background-color: #00000015 !important;
    }

    &-warning {
        color: #664d03;
        background-color: #fff3cd;
        border-color: #ffecb5;
        /* color: #aa4d00; */
        /* background: #fff6de; */
        border-color: #ffdc73;
    }

    &-danger {
        border-color: var(--border-red-100) !important;
        color: var(--error-400) !important;
        background-color: #ffeeef !important;
    }

    &-info {
        color: #313b46 !important;;
        background-color: #f9fbfc !important;
        border-color: rgba(0, 0, 0, .05) !important;
    }
}
</style>